/*
 * GEHC CONFIDENTIAL
 *
 * Copyright (c) 2026, 2026, GE HealthCare
 * All Rights Reserved
 *
 * This unpublished material is proprietary to GE HealthCare. The methods and
 * techniques described herein are considered trade secrets and/or
 * confidential. Reproduction or distribution, in whole or in part, is
 * forbidden except by express written permission of GE HealthCare.
 * GE is a trademark of General Electric Company used under trademark license.
 */

import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { UtilService } from '@app/util/util.service';

/**
 * Centralised FHIRcast hub.topic store.
 *
 * All integrations that share the same FHIRcast hub (DA, EV) must use the same
 * `hub.topic` so that events published by one party are received by the other.
 * This service holds the single authoritative topic and notifies all integrations
 * whenever the topic is renewed (e.g. after a WebSocket reconnect).
 */
@Injectable({
  providedIn: 'root'
})
export class FhircastTopicService {
  private _currentTopic = UtilService.getUUID();
  private readonly _topicRenewed$ = new Subject<string>();

  /**
   * The active `hub.topic` UUID shared by all FHIRcast integrations.
   * Always read this inside `createSubscription()` so a renewal is picked up
   * before re-registering with the hub.
   */
  get currentTopic(): string {
    return this._currentTopic;
  }

  /**
   * Emits the `sourceId` (APP_ID) of the integration that triggered a renewal.
   * Filter by `sourceId !== myAppId` to avoid reacting to your own renewals.
   */
  readonly topicRenewed$ = this._topicRenewed$.asObservable();

  /**
   * Generates a fresh UUID, stores it as the current topic, and notifies all
   * subscribers via `topicRenewed$`.
   *
   * @param sourceId  APP_ID of the integration initiating the renewal so that
   *                  service can ignore its own emit.
   * @returns the new topic string.
   */
  renewTopic(sourceId: string): string {
    this._currentTopic = UtilService.getUUID();
    this._topicRenewed$.next(sourceId);
    return this._currentTopic;
  }
}
